import { useState, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import UploadRetryIcon from '../../assets/icon/upload-retry.svg';
import {
  checkImageResourceStatus,
  calculateMImageSize,
  ImageResourceStatus,
} from './util';
import CommonReactImage from '@/components/CommontReactImage';
import Styles from './mIndex.module.scss';

const defaultCover = require('../../assets/icon/photo-default-cover.png');
const defaultAuditCover = require('../../assets/icon/photo-default-audit.png');


export interface IImgSize {
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
  scale: number;
}

export interface IPhotoPreview {
  className?: string;
  id: string;
  url: string;
  ratio?: string;
  isEdit?: boolean;
  percent?: number;
  completed?: boolean;
  error?: any;
  onClick?: (key: string) => void;
  onRemove: (key: string) => void;
  onRetry?: (key: string) => void;
}

const PhotoPreview = ({
  className = '',
  id,
  url,
  percent = url.indexOf("https://") > -1 ? 1 : 0,
  completed,
  isEdit,
  error,
  onClick = () => {},
  onRetry = () => {},
  onRemove,
}: IPhotoPreview) => {
  const [imgLoadStatus, setImgLoadStatus] = useState<ImageResourceStatus>();
  const [imgSize, setImgSize] = useState<IImgSize>({} as IImgSize);
  const t = useTranslations();
  const ref = useRef<any>();

  const handleClick = () => {
    if (isEdit || !url || imgLoadStatus !== ImageResourceStatus.success) {
      return;
    }

    onClick(id);
  };

  const handleRemove = () => {
    onRemove(id);
  };

  const handleUploadRetry = () => {
    onRetry(id);
  };

  const customLoader = ({ src }: { src: string }) => {
    return src;
  }

  const onLoad = (item:any) => {
    const { naturalWidth, naturalHeight, width, height } = item.currentTarget;
    setImgSize({
      naturalWidth: naturalWidth*2,
      naturalHeight: naturalHeight*2,
      width,
      height,
      scale: Number(parseFloat((naturalWidth / width).toString())),
    });
  }

  useEffect(() => {
    // 图片不存在
    if (!url) {
      setImgLoadStatus(ImageResourceStatus.notFound);
      return;
    }

    // 本地图片
    if (url.startsWith('blob:')) {
      setImgLoadStatus(ImageResourceStatus.local);
      return;
    }

    // 检查图片资源状态
    checkImageResourceStatus(url, (statusCode) => setImgLoadStatus(statusCode));
  }, [url]);

  const imgStyle = useMemo(() => {
    return calculateMImageSize(ref?.current?.getBoundingClientRect().width, imgSize.naturalWidth, imgSize.naturalHeight);
  }, [ref?.current?.getBoundingClientRect, imgSize.naturalWidth, imgSize.naturalHeight]);

  // 判断是否为特定错误码，需要显示错误信息而不是重试
  const isSpecificError = useMemo(() => {
    if (!error) return false;
    
    // 支持多种错误码格式
    const errorCode = error.code || error.error_code || error.errorCode;
    const numericCode = typeof errorCode === 'string' ? parseInt(errorCode) : errorCode;
    
    // 检查错误码
    if (numericCode === 10032 || numericCode === 10033) {
      return true;
    }
    
    // 如果错误是字符串，检查是否包含GIF相关的错误信息
    const errorText = typeof error === 'string' ? error : error.message || '';
    const isGifError = errorText.includes('gif') || errorText.includes('GIF') || 
                      errorText.includes('帧数') || errorText.includes('图片数量');
    
    return isGifError;
  }, [error]);

  return (
    <div
      className={cx(Styles.root, className)}
      style={
        imgStyle
          ? { width: imgStyle.imgWidth, height: imgStyle.imgHeight, visibility: 'visible' }
          : { width: '100%', height: '200px', visibility: (imgLoadStatus || !url) ? 'visible' : 'hidden' }
      }
      onClick={handleClick}
      ref={ref}
    >
      {
        isEdit && <div className={Styles.remove} onClick={handleRemove} />
      }
      {
        isEdit && (percent < 1 || !completed) && <div className={Styles.tips}>{t('tips_thread_photo_percent', { percent: Math.min(Number.parseInt((percent * 100).toString()), 99) })}</div>
      }
      {
        (imgLoadStatus && imgLoadStatus !== ImageResourceStatus.success) || !url ? (
          <div className={Styles.defaultImgWrapper}>
            {
              imgLoadStatus && imgLoadStatus !== ImageResourceStatus.local && (
                <Image
                  src={imgLoadStatus === ImageResourceStatus.audit ? defaultAuditCover : defaultCover}
                  loader={customLoader}
                  alt="pic"
                  width={80}
                  height={80}
                  className={Styles.img}
                />
              )
            }
            {
              imgLoadStatus === ImageResourceStatus.audit && <div className={Styles.auditTips}>{t('tips_image_audit')}</div>
            }
          </div>
        ) : (
          <CommonReactImage
            className={Styles.mImg}
            src={url}
            alt="pic"
            onLoad={onLoad}
            onClick={handleClick}
            style={{
              width: '100%',
              borderRadius: 4,
            }}
          />
        )
      }
      {
        !!error && (
                     // 如果是特定错误码（10032或10033），显示错误信息，不显示重试按钮
           isSpecificError ? (
             <div className={Styles.errorWrapper}>
               <div className={Styles.errorTips} style={{color: '#ff4d4f', textAlign: 'center', padding: '8px'}}>
                 {typeof error === 'string' ? error : error.message}
               </div>
             </div>
           ) : (
            <div className={Styles.errorWrapper}>
              <div className={Styles.errorIcon} onClick={handleUploadRetry}>
                <UploadRetryIcon />
              </div>
              <div className={Styles.errorTips}>{t('tips_thread_upload_retry')}</div>
            </div>
          )
        )
      }
      {
        !url && !error && <div className={Styles.defaultIcon} />
      }
    </div>
  );
};

export default PhotoPreview;
